// Hosted Model Serving (Part II) — the small building blocks the page, dialogs and
// drawer share (badge, button, dialog, field, notice, progress bar). Styling only;
// decisions live in modelServingModel.js.
import { createPortal } from "react-dom";
import { COLORS } from "../../theme/colors";
import { statusBadge } from "./modelServingModel";

export const MONO = "ui-monospace,SFMono-Regular,Menlo,monospace";

const TONE = () => ({
  green: COLORS.green, amber: COLORS.orange, dim: COLORS.textDim, purple: COLORS.purple,
  pink: COLORS.pink, cyan: COLORS.cyan,
});

export const card = () => ({
  background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 16,
});

export const input = () => ({
  width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 10, fontSize: 13,
  border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text,
});

const label = () => ({
  fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.textDim,
  marginBottom: 5, display: "block", fontWeight: 600,
});

export function Badge({ status, label: text, tone, lc, testid = "ms-badge" }) {
  const b = status ? statusBadge(status) : { label: text, tone };
  const color = TONE()[b.tone];
  return (
    <span data-testid={testid} data-status={status || b.tone} data-lc={lc}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 9px", borderRadius: 999,
        fontSize: 11.5, fontWeight: 600, color, border: `1px solid ${color}55`, whiteSpace: "nowrap",
      }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />{b.label}
    </span>
  );
}

export function Dot({ tone, pulse }) {
  const color = TONE()[tone];
  return (
    <span data-testid="ms-dot" data-tone={tone}
      style={{ width: 9, height: 9, borderRadius: "50%", background: color, display: "inline-block",
        boxShadow: pulse ? `0 0 0 3px ${color}33` : "none" }} />
  );
}

export function Btn({ kind = "default", sm, children, style, ...rest }) {
  const primary = kind === "primary";
  const danger = kind === "danger";
  return (
    <button type="button" {...rest}
      style={{
        padding: sm ? "6px 12px" : "8px 14px", borderRadius: 999, fontSize: sm ? 12.5 : 13,
        fontWeight: 600, cursor: rest.disabled ? "not-allowed" : "pointer", opacity: rest.disabled ? 0.55 : 1,
        border: `1px solid ${primary ? "transparent" : danger ? `${COLORS.pink}66` : COLORS.border}`,
        background: primary ? COLORS.accent : kind === "ghost" ? "transparent" : COLORS.surface,
        color: primary ? "#F0F0F8" : danger ? COLORS.pink : COLORS.text, whiteSpace: "nowrap", ...style,
      }}>
      {children}
    </button>
  );
}

// Rendered at <body> (a portal): the admin content is its own stacking layer below
// the app header, so anything fixed inside it is covered by the header (staging
// 2026-10-08: the drawer's title was hidden). Same level as the shared Modal (1000).
export const LAYER = { scrim: 1000, drawer: 1001, dialog: 1100 };

export const onTop = (node) => createPortal(node, document.body);

/** A modal dialog. Dialogs sit above the drawer (I6); closing one keeps the drawer. */
export function Dialog({ testid, title, sub, glyph, onClose, children, footer, maxWidth = 520 }) {
  return onTop(
    <div data-testid={testid} role="dialog" aria-modal="true" aria-label={title}
      style={{
        position: "fixed", inset: 0, background: "rgba(5,7,16,.55)", display: "flex",
        alignItems: "flex-start", justifyContent: "center", padding: "48px 16px", overflow: "auto",
        zIndex: LAYER.dialog,
      }}>
      <div style={{ ...card(), width: "100%", maxWidth, borderRadius: 20, padding: 0, position: "relative" }}>
        <button type="button" aria-label="Close" onClick={onClose}
          style={{ position: "absolute", top: 12, right: 12, border: "none", background: "transparent",
            color: COLORS.textDim, fontSize: 16, cursor: "pointer" }}>✕</button>
        <div style={{ display: "flex", gap: 12, padding: "20px 22px 8px" }}>
          {glyph && (
            <div style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, display: "grid", placeItems: "center",
              background: COLORS.accentSoft, border: `1px solid ${COLORS.border}`, fontSize: 16 }}>{glyph}</div>
          )}
          <div style={{ paddingRight: 24 }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 17, color: COLORS.text }}>{title}</h3>
            {sub && <p style={{ margin: 0, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.5 }}>{sub}</p>}
          </div>
        </div>
        {children && <div style={{ padding: "8px 22px 4px" }}>{children}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "14px 22px 20px", flexWrap: "wrap" }}>
          {footer}
        </div>
      </div>
    </div>,
  );
}

export function Field({ label: text, hint, htmlFor, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {text && <label htmlFor={htmlFor} style={label()}>{text}</label>}
      {children}
      {hint && <div style={{ fontSize: 11.5, color: COLORS.textDim, marginTop: 5, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

/** A tinted notice line: tone "warn" (amber), "bad" (pink), "ok" (green), "info". */
export function Notice({ icon, tone = "warn", lc, testid, children }) {
  const color = { warn: COLORS.orange, bad: COLORS.pink, ok: COLORS.green, info: COLORS.cyan }[tone];
  return (
    <div data-testid={testid} data-lc={lc}
      style={{ display: "flex", gap: 10, padding: "10px 12px", borderRadius: 12, fontSize: 12.5, lineHeight: 1.5,
        color: COLORS.textMuted, background: `${color}14`, border: `1px solid ${color}40`, marginBottom: 12 }}>
      <span aria-hidden="true">{icon}</span><div>{children}</div>
    </div>
  );
}

export function Progress({ pct }) {
  return (
    <div data-testid="ms-progress" data-pct={pct}
      style={{ height: 5, borderRadius: 999, background: `${COLORS.border}`, overflow: "hidden", margin: "5px 0 3px" }}>
      <i style={{ display: "block", height: "100%", width: `${pct}%`, background: COLORS.purple }} />
    </div>
  );
}

export function SectionHead({ title, count }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "24px 0 10px" }}>
      <h3 style={{ margin: 0, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.4, color: COLORS.textMuted }}>{title}</h3>
      {count != null && (
        <span style={{ fontSize: 11.5, color: COLORS.textDim, border: `1px solid ${COLORS.border}`, borderRadius: 999,
          padding: "0 8px" }}>{count}</span>
      )}
    </div>
  );
}

export const errorText = (e) => e?.message || String(e);
